/**
 * @copyright 2023 Nuance Communications Inc.
 * All Rights Reserved.
 */

import {
    IDropdownOption,
    ITextStyles,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    Stack,
    StackItem,
    Text
} from "@fluentui/react";
import {
    AadNmsConnectorSvc,
    IEntraLinkingRequestDto,
    IOrganization,
    MsftGraphSvc,
    useApiRequest,
    useHapAuth,
    UserManagementSvc,
    WORKFLOW_SVC_CACHE_KEYS,
    WorkflowSvc
} from "@nuance/hap-components";
import {
    HuxDropdown,
    HuxPageHeader,
    IHuxPageActionProps,
    NoItemsAvailable,
    NoItemsType
} from "@nuance/hux-components";
import { getLogger, isValidGuid } from "@nuance/hux-diagnostics";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { EntraLinkingCard } from "./EntraLinkingCard";
import { useBoolean } from "@fluentui/react-hooks";
import { EventTypes } from "../EventTypes";
import { SelectedGroup, SelectGroupsPanel } from "../components/SelectGroupsPanel";
import { useMsal } from "@azure/msal-react";
import { useQueryClient } from "@tanstack/react-query";
import { FailedToLoadPage } from "../components/FailedToLoadPage";

const textStyle: Partial<ITextStyles> = {
    root: {
        padding: 0,
        whiteSpace: "pre-line",
        resize: "none",
        fontWeight: 500
    }
};

const logger = getLogger();
/**
 * The entra user link page.
 * @returns - The entra user link.
 */
const EntraUserLinkPage = (): JSX.Element => {
    const { t } = useTranslation();
    const { orgGuid } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { instance: clientApp } = useMsal();
    const { userContext, tenantId } = useHapAuth();

    if (!orgGuid || !isValidGuid(orgGuid)) return <FailedToLoadPage />;
    if (!tenantId) return <FailedToLoadPage />;

    const [isStartNewMappingPanelOpen, { toggle: toggleIsStartNewMappingPanelOpen }] =
        useBoolean(false);

    const [refetch, setRefetch] = useState(true);
    const [org, setOrg] = useState<IOrganization>({} as IOrganization);
    const userManagementSvc = new UserManagementSvc(userContext);
    const aadNmsConnectorSvc = new AadNmsConnectorSvc(userContext);
    const workflowSvc = new WorkflowSvc(userContext);
    const msftGraphSvc = new MsftGraphSvc(clientApp as never, userContext);
    const orgDataRequest = useApiRequest(userManagementSvc.selectOrganizationByGuid(orgGuid), {
        refetchOnWindowFocus: refetch
    });
    const orgGroupsRequest = useApiRequest(userManagementSvc.getGroupsByOrg(org.UID), {
        enabled: org.UID !== 0 && org.UID !== undefined,
        refetchOnWindowFocus: refetch
    });
    const getOrgsForTenantRequest = useApiRequest(
        aadNmsConnectorSvc.getNMSOrganizationsForAADTenant(tenantId),
        {
            refetchOnWindowFocus: refetch
        }
    );
    const getMyEntraLinkingRequest = useApiRequest(workflowSvc.getEntraLinkingHistory(), {
        refetchOnWindowFocus: refetch
    });

    useEffect(() => {
        if (orgDataRequest.data) {
            setOrg(orgDataRequest.data);
        }
    }, [orgDataRequest.data]);
    useEffect(() => {
        document.title = t("EntraUserLink.Page_Title");
    }, []);
    const requests = [
        orgDataRequest,
        getOrgsForTenantRequest,
        getMyEntraLinkingRequest,
        orgGroupsRequest
    ];
    const isLoading = requests.some(request => request.isLoading);
    const isError = requests.some(request => request.isError);

    if (isError) {
        if (refetch) setRefetch(false);
        return <FailedToLoadPage />;
    }

    if (
        isLoading ||
        !orgDataRequest.data ||
        !getOrgsForTenantRequest.data ||
        !getMyEntraLinkingRequest.data ||
        !orgGroupsRequest.data
    ) {
        return (
            <Spinner
                size={SpinnerSize.large}
                style={{ height: "80%" }}
                ariaLabel={t("Progress.Spinner_Loading")}
                label={t("Progress.Spinner_Loading")}
            />
        );
    }

    const tenantInfo = getOrgsForTenantRequest.data.find(org => org.organizationGuid === orgGuid);

    if (!tenantInfo) {
        return <FailedToLoadPage />;
    }

    const options: IDropdownOption[] = getOrgsForTenantRequest.data.map(org => ({
        key: org.organizationGuid,
        text: org.organizationName
    }));

    const entraLinking = getMyEntraLinkingRequest.data
        .filter(linking => linking.organizationGuid === orgGuid)
        .map(linking => {
            return {
                ...linking,
                linkTime: { convertToLocaleDateTime: new Date(linking.submittedAt) },
                groupNames: linking.groupUIDs
                    ?.map(uid => {
                        const group = orgGroupsRequest.data.find(g => g.UID === uid);
                        return group ? group.Name : "Unknown Group";
                    })
                    .join(", ")
            };
        })
        .sort((a, b) => {
            return new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime();
        });

    const startNewMappingClickHandler = () => {
        logger.trackEvent(EventTypes.EntraLinkingPageStartNewMappingButtonClicked);
        toggleIsStartNewMappingPanelOpen();
    };

    const startNewMapping = async (selected: SelectedGroup[]) => {
        const selectedGroups = selected.map(s => s.groupUID);
        const graphAccessToken = await msftGraphSvc.getGraphToken();
        if (!graphAccessToken) {
            logger.logError(
                "Failed to get Microsoft Graph access token while starting new Entra linking mapping."
            );
            return;
        }
        const dto: IEntraLinkingRequestDto = {
            GraphAccessToken: graphAccessToken,
            GroupUIDs: selectedGroups.length > 0 ? selectedGroups : undefined
        };
        workflowSvc
            .scheduleEntraLinking(dto, orgGuid)
            .execute2()
            .then(() => {
                /**
                 * Track the event
                 */
                logger.trackEvent(EventTypes.NewMappingInitialized, {
                    userGuid: userContext.user.guid
                });

                /**
                 * Invalidate the cache
                 */
                queryClient.invalidateQueries({
                    queryKey: [WORKFLOW_SVC_CACHE_KEYS.entraLinkingHistory, userContext.user.guid]
                });
                toggleIsStartNewMappingPanelOpen();
            })
            .catch(() => {
                logger.logError("Error scheduling entra mapping");
                return t("EntraUserLink.New_Mapping_Failure_Message");
            });
    };

    const actions: IHuxPageActionProps = {
        otherActions: [],
        secondaryAction:
            getOrgsForTenantRequest.data.length > 1
                ? {
                      commandBarButtonAs: () => {
                          return (
                              <HuxDropdown
                                  placeholder={t("EntraUserLink.Change_Organization")}
                                  options={options}
                                  styles={{
                                      root: { flexBasis: 500 },
                                      dropdown: { border: "none" },
                                      title: { border: "none" }
                                  }}
                                  defaultSelectedKey={"default"}
                                  calloutProps={{ calloutMaxHeight: 400 }}
                                  onChange={(_, option) => {
                                      if (option && option.key && option.key !== "default") {
                                          navigate(`/EntraUserLink/${option.key}`);
                                      }
                                  }}
                              />
                          );
                      },
                      key: "orgsDropdown"
                  }
                : undefined,
        primaryAction: {
            commandBarButtonAs: () => {
                return (
                    <PrimaryButton
                        styles={{ root: { height: "44px" } }}
                        ariaLabel={t("EntraUserLink.Start_New_Mapping")}
                        onClick={startNewMappingClickHandler}
                    >
                        {t("EntraUserLink.Start_New_Mapping")}
                    </PrimaryButton>
                );
            },
            key: "startNewMapping"
        }
    };
    return (
        <main tabIndex={-1}>
            <Stack>
                <StackItem styles={{ root: { marginLeft: 22, marginTop: 10 } }}>
                    <Text styles={textStyle}>
                        {t("EntraUserLink.Tenant")}: {tenantInfo?.tenantName}
                    </Text>
                </StackItem>
                <StackItem>
                    <HuxPageHeader
                        actions={actions}
                        titleInfo={{
                            parentTitle: orgDataRequest.data.Name,
                            title: t("EntraUserLink.EntraUserLink_Title")
                        }}
                    />
                </StackItem>
                <StackItem>
                    <Stack
                        tokens={{ childrenGap: "24px" }}
                        style={{ padding: "24px", height: "100%" }}
                    >
                        {entraLinking.length === 0 && (
                            <StackItem>
                                <NoItemsAvailable
                                    heading={t("EntraUserLink.No_Mapping_Found")}
                                    subHeading={t("EntraUserLink.No_Mapping_Found_Subheading")}
                                    type={NoItemsType.UploadAFile}
                                ></NoItemsAvailable>
                            </StackItem>
                        )}
                        {entraLinking.length > 0 &&
                            entraLinking.map(linking => {
                                return (
                                    <EntraLinkingCard
                                        key={linking.entraLinkingTaskGuid}
                                        entraLinkingInfo={linking}
                                        orgName={orgDataRequest.data.Name}
                                        groupNames={linking.groupNames}
                                    ></EntraLinkingCard>
                                );
                            })}
                    </Stack>
                </StackItem>
            </Stack>
            {isStartNewMappingPanelOpen && (
                <SelectGroupsPanel
                    onDismiss={() => {
                        toggleIsStartNewMappingPanelOpen();
                    }}
                    onAdd={async (selected: SelectedGroup[]) => {
                        await startNewMapping(selected);
                    }}
                    orgUID={orgDataRequest.data.UID}
                    selected={[]}
                    headerText={t("EntraUserLink.New_Mapping_Title")}
                    submitButtonText={t("EntraUserLink.Map_Button_Title")}
                    spinnerText={t("EntraUserLink.Spinner_Starting_Mapping")}
                    enableSubmitButtonWithoutSelection={true}
                    showOnlyGroupsWhichUserCanEnroll={true}
                    optionalSecondaryNoteText={t("EntraUserLink.Select_Groups_Tip")}
                ></SelectGroupsPanel>
            )}
        </main>
    );
};

export default EntraUserLinkPage;
